import { useCallback, useEffect, useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";
import { useHighContrast } from "../lib/a11y";

// Settings surface — consumer-controlled toggles:
//   * Autonomy profile (default max tier + amount bands)
//   * Visual prefs (top style clusters)
//   * Emergency pre-authorisation (medical disclosure scopes)
//
// Twin profile edit is a separate sub-surface; not implemented in
// detail here — the cell-by-cell editor is its own sprint.

interface AutonomyProfile {
  consumer_id: string;
  default_max_tier: string;
  per_vertical_max: Record<string, string>;
  amount_bands: { threshold_yen: number; tier: string }[];
  new_provider_min_tier: string;
}

interface VisualVector {
  weights?: Record<string, number>;
}


const AUTONOMY_TIERS = [
  "TIER_1_AUTONOMOUS",
  "TIER_2_NOTIFY",
  "TIER_3_PRE_APPROVE",
  "TIER_4_PER_STEP",
];

const EMERGENCY_SCOPES = [
  "allergies", "blood_type", "medications", "emergency_contacts",
];


export function SettingsSurface() {
  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      <h1 className="text-base font-medium text-(--color-ink)">Settings</h1>
      <AutonomyPanel />
      <EmergencyPanel />
      <VisualPrefsPanel />
      <A11yPanel />
    </div>
  );
}


function A11yPanel() {
  const { enabled, setEnabled } = useHighContrast();
  return (
    <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
      <h2 className="mb-1 text-xs uppercase tracking-wide text-(--color-ink-faint)">
        Accessibility
      </h2>
      <p className="mb-2 text-xs text-(--color-ink-muted)">
        Display + interaction adjustments. Per JIS X 8341-3 + WCAG 2.2.
      </p>
      <label className="flex items-center justify-between text-xs">
        <span>High contrast theme</span>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          aria-label="Toggle high-contrast theme"
        />
      </label>
      <p className="mt-2 text-xs text-(--color-ink-faint)">
        Reduced-motion is honoured automatically when set in your OS.
      </p>
    </section>
  );
}


// ── Autonomy ───────────────────────────────────────────────────────


function AutonomyPanel() {
  const [profile, setProfile] = useState<AutonomyProfile | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const p = await mcpCall<AutonomyProfile>(
        "autonomy.get_profile", { consumer_id: consumerIdOrThrow() },
      );
      setProfile(p);
    } catch (e) { setErr((e as Error).message); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const setDefault = async (tier: string) => {
    try {
      await mcpCall("autonomy.set_default_max",
        { consumer_id: consumerIdOrThrow(), tier });
      load();
    } catch (e) { setErr((e as Error).message); }
  };

  return (
    <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
      <h2 className="mb-1 text-xs uppercase tracking-wide text-(--color-ink-faint)">
        Autonomy
      </h2>
      <p className="mb-2 text-xs text-(--color-ink-muted)">
        How much room your agents have to act without asking.
      </p>
      {err && <p className="text-xs text-red-300">{err}</p>}
      {profile && (
        <div className="space-y-2 text-xs">
          <label className="block">
            <span className="block mb-1 text-(--color-ink-faint)">
              Default ceiling
            </span>
            <select
              value={profile.default_max_tier}
              onChange={(e) => setDefault(e.target.value)}
              className="w-full rounded-md bg-(--color-canvas-elev) px-2 py-1 text-(--color-ink)"
            >
              {AUTONOMY_TIERS.map((t) => (
                <option key={t} value={t}>{t.replace("_", " ").toLowerCase()}</option>
              ))}
            </select>
          </label>
          <div>
            <p className="mb-1 text-(--color-ink-faint)">Amount bands</p>
            <ul className="space-y-0.5 text-(--color-ink-muted)">
              {profile.amount_bands.map((b, i) => (
                <li key={i} className="flex justify-between">
                  <span>≥ ¥{b.threshold_yen.toLocaleString()}</span>
                  <span className="font-mono text-(--color-ink-faint)">
                    {b.tier}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}


// ── Emergency ──────────────────────────────────────────────────────


function EmergencyPanel() {
  const [authorised, setAuthorised] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (scopes: string[]) => {
    setBusy(true);
    setErr(null);
    try {
      const result = await mcpCall<{ authorised_scopes: string[] }>(
        "emergency.pre_authorise",
        { consumer_id: consumerIdOrThrow(), scopes },
      );
      setAuthorised(result.authorised_scopes);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const toggle = (scope: string) => {
    const next = authorised.includes(scope)
      ? authorised.filter((s) => s !== scope)
      : [...authorised, scope];
    void save(next);
  };

  return (
    <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
      <h2 className="mb-1 text-xs uppercase tracking-wide text-(--color-ink-faint)">
        Emergency disclosure
      </h2>
      <p className="mb-2 text-xs text-(--color-ink-muted)">
        Pre-authorise what your agents may share with hospitals if an
        emergency activates. Revocable any time.
      </p>
      {err && <p className="text-xs text-red-300">{err}</p>}
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        {EMERGENCY_SCOPES.map((s) => {
          const on = authorised.includes(s);
          return (
            <button
              key={s}
              disabled={busy}
              onClick={() => toggle(s)}
              className={[
                "rounded-md px-2 py-1.5 text-left transition",
                on ? "bg-(--color-accent-gold) text-stone-900"
                   : "bg-(--color-canvas-elev) text-(--color-ink-muted) hover:text-(--color-ink)",
              ].join(" ")}
            >
              {on ? "✓ " : ""}{s.replace("_", " ")}
            </button>
          );
        })}
      </div>
    </section>
  );
}


// ── Visual prefs ───────────────────────────────────────────────────


function VisualPrefsPanel() {
  const [vector, setVector] = useState<VisualVector | null>(null);
  const [vertical, setVertical] = useState("travel.hotel");

  const load = useCallback(async () => {
    try {
      const v = await mcpCall<VisualVector>("visual_pref.get_vector",
        { consumer_id: consumerIdOrThrow(), vertical });
      setVector(v);
    } catch {
      setVector({ weights: {} });
    }
  }, [vertical]);

  useEffect(() => { void load(); }, [load]);

  const top = Object.entries(vector?.weights || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  return (
    <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
      <h2 className="mb-1 text-xs uppercase tracking-wide text-(--color-ink-faint)">
        Visual preferences
      </h2>
      <select
        value={vertical}
        onChange={(e) => setVertical(e.target.value)}
        className="mb-2 rounded-md bg-(--color-canvas-elev) px-2 py-1 text-xs text-(--color-ink)"
      >
        <option value="travel.hotel">travel.hotel</option>
        <option value="food">food</option>
        <option value="fashion">fashion</option>
        <option value="wellness">wellness</option>
      </select>
      {top.length === 0 ? (
        <p className="text-xs text-(--color-ink-faint)">
          No signal yet — your style vector seeds at onboarding or as
          you tap cards.
        </p>
      ) : (
        <ul className="space-y-1 text-xs">
          {top.map(([k, v]) => (
            <li key={k} className="flex items-baseline gap-2">
              <span className="flex-1 truncate text-(--color-ink-muted)">{k}</span>
              <span className="h-1 w-12 bg-(--color-canvas-elev) rounded-full overflow-hidden">
                <span
                  className="block h-full bg-(--color-accent-gold)"
                  style={{ width: `${Math.round(v * 100)}%` }}
                />
              </span>
              <span className="tabular-nums text-(--color-ink-faint) w-8 text-right">
                {Math.round(v * 100)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
